import { initializeApp, type FirebaseApp } from 'firebase/app';
import { type Auth } from 'firebase/auth';
import { getFirestore, type Firestore } from 'firebase/firestore';
import { type FirebaseStorage } from 'firebase/storage';
import { type Analytics } from 'firebase/analytics';
import { type Messaging } from 'firebase/messaging';

import {
  getFirebaseConfig,
  getFirebaseEnvironment,
  validateFirebaseConfig,
} from './firebaseConfig';
import { logger } from '../core/env';
import type { FirebaseInitResult, FirebaseServiceOptions } from '../../types/firebase';

// Global Firebase app instance
let firebaseApp: FirebaseApp | null = null;

// Global service instances
let firestore: Firestore | null = null;

export const initializeFirebase = (options: FirebaseServiceOptions = {}): FirebaseInitResult => {
  try {
    // Return existing app if already initialized
    if (firebaseApp) {
      logger.debug('Firebase app already initialized, returning existing instance');
      return {
        app: firebaseApp,
        config: getFirebaseConfig(),
        environment: getFirebaseEnvironment(),
      };
    }

    // Get configuration for current environment
    const config = getFirebaseConfig();
    const environment = getFirebaseEnvironment();

    // Validate configuration
    if (!validateFirebaseConfig(config)) {
      throw new Error('Invalid Firebase configuration');
    }

    logger.info(`Initializing Firebase for environment: ${environment}`);
    logger.debug('Firebase config:', {
      projectId: config.projectId,
      authDomain: config.authDomain,
    });

    // Initialize Firebase app
    firebaseApp = initializeApp(config);

    if (options.enableFirestore !== false) {
      firestore = getFirestore(firebaseApp);
      logger.debug('Firebase Firestore initialized');
    }

    // Initialize Messaging only in browser environment
    if (options.enableMessaging !== false && typeof window !== 'undefined') {
      try {
        logger.debug('Firebase Messaging initialized');
      } catch (error) {
        logger.warn('Firebase Messaging initialization failed:', error);
      }
    }

    logger.info('Firebase initialization completed successfully');

    return {
      app: firebaseApp,
      config,
      environment,
    };
  } catch (error) {
    logger.error('Firebase initialization failed:', error);
    throw new Error(
      `Failed to initialize Firebase: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
};
export const getFirebaseFirestore = (): Firestore => {
  if (!firestore) {
    initializeFirebase({ enableFirestore: true });
    if (!firestore) {
      throw new Error('Failed to initialize Firebase Firestore');
    }
  }
  return firestore;
};
// Auto-initialize Firebase when this module is imported
try {
  initializeFirebase();
} catch (error) {
  logger.error('Auto-initialization of Firebase failed:', error);
}

// Export Firebase services for direct use
export {
  type FirebaseApp,
  type Auth,
  type Firestore,
  type FirebaseStorage,
  type Analytics,
  type Messaging,
};
